import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { getMyFeatureAccess } from '../modules/platform/api/featureAccess';
import type { SchoolStudentFeatureKey } from '../modules/platform/types/featureAccess';
import { useAuth } from './AuthContext';
import { userHasStudentRole } from '../utils/lmsLaunch';

interface FeatureAccessContextType {
  /** True while auth is loading or feature access is fetching for a student. */
  loading: boolean;
  /** Whether the feature-access request failed (gated modules treated as disabled). */
  error: boolean;
  tenantId: string | null;
  /** For the six mapped keys: true only when API returns true; missing/false/error → false (fail-closed). */
  isSchoolFeatureEnabled: (key: SchoolStudentFeatureKey) => boolean;
  refetch: () => Promise<void>;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | undefined>(undefined);

export const useFeatureAccess = (): FeatureAccessContextType => {
  const ctx = useContext(FeatureAccessContext);
  if (!ctx) {
    return {
      loading: false,
      error: false,
      tenantId: null,
      isSchoolFeatureEnabled: () => false,
      refetch: async () => {},
    };
  }
  return ctx;
};

interface FeatureAccessProviderProps {
  children: ReactNode;
}

export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const isStudent = userHasStudentRole(user);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [accessMap, setAccessMap] = useState<Record<string, boolean> | null>(null);

  const membershipKey = user?.last_active_membership_key ?? user?.memberships?.[0]?.membership_key ?? '';

  const refetch = useCallback(async () => {
    if (!user || !isStudent) {
      setAccessMap(null);
      setTenantId(null);
      setError(false);
      setFetchLoading(false);
      return;
    }
    setFetchLoading(true);
    setError(false);
    try {
      const data = await getMyFeatureAccess();
      setTenantId(data.tenant_id ?? null);
      setAccessMap(data.school_feature_access ?? {});
    } catch (e) {
      console.error('Failed to load feature access:', e);
      setAccessMap(null);
      setTenantId(null);
      setError(true);
    } finally {
      setFetchLoading(false);
    }
  }, [user, isStudent]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isStudent) {
      setAccessMap(null);
      setTenantId(null);
      setError(false);
      setFetchLoading(false);
      return;
    }
    void refetch();
  }, [authLoading, user?.id, isStudent, membershipKey, refetch]);

  const isSchoolFeatureEnabled = useCallback(
    (key: SchoolStudentFeatureKey): boolean => {
      if (!isStudent || !accessMap) return false;
      return accessMap[key] === true;
    },
    [isStudent, accessMap]
  );

  const loading = authLoading || (isStudent && fetchLoading);

  const value = useMemo(
    (): FeatureAccessContextType => ({
      loading,
      error,
      tenantId,
      isSchoolFeatureEnabled,
      refetch,
    }),
    [loading, error, tenantId, isSchoolFeatureEnabled, refetch]
  );

  return <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>;
};
